import { useParams, useLocation, useHistory } from "react-router-dom";
import {
  AnswerReference,
  DataRoomFile,
  DataRoomPermission,
  DealActivityType,
  FileType,
  QuestionQuery,
  SuggestedAnswerStatus,
  SuggestedAnswerBlockType,
  useCreateCommentMutation,
  useCreateQuestionAnswerMutation,
  useQuestionQuery,
  useTrackActivityMutation,
  useDataRoomFolderQuery,
  useDataRoomQuery,
  QuestionStatus,
  useCreateQuestionAnswerThreadMessageMutation,
  useUpdateQuestionMutation,
  QuestionPriority,
  DataRoomQuery,
  DataRoomFolder,
  PermissionsResourceType,
  DealRoleType,
  useQuestionAccessQuery,
  AccessControlDealGroup,
  useDeleteQuestionMutation
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddFileModal } from "../../components/AddFileModal";
import { formatDistanceToNow, fromUnixTime } from "date-fns";
import { FilePill } from "../../components/FilePill";
import { Avatar } from "../../components/account/Avatar";
import { useSelector } from "react-redux";
import { authSelectors } from "../../store/auth/selector";
import { useQueryClient } from "@tanstack/react-query";
import { Button, ButtonPopout } from "../../components/tailwind/Button";
import { Pills } from "../../components/Pills";
import { ActivityItem } from "../../components/activity/ActivityItem";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import Markdown from "react-markdown";
import { classNames } from "@/src/utils/cn";
import Loading from "../../components/Loading";
import {
  BotIcon,
  ChevronUpIcon,
  PlusIcon,
  UploadIcon,
  FolderPlusIcon,
  PencilIcon,
  ArrowUpIcon,
  CircleDashedIcon,
  CheckCircleIcon,
  CircleXIcon,
  TrashIcon,
  CopyIcon,
  CheckIcon,
  BrainIcon,
  SparklesIcon
} from "lucide-react";
import {
  InlineFileClickData,
  fileRefKey,
  stripInlineRefs,
} from "@/src/components/ai/markdown";
import { AiMessageReasoningBlock, formatReasoningDurationMs } from "@/src/components/ai/reasoning";
import { AiMessageToolCallBlock, buildToolSummaryTokens } from "@/src/components/ai/tool-calls";
import { useWebSocket } from "@/src/contexts/websockets";
import { FileIcon } from "@/src/components/FileIcon";
import { Spinner } from "@/src/components/icons/Spinner";
import {
  AnswerEditor,
  AnswerViewer,
  FileRefClickProvider,
  type FileRefClickData,
} from "@/src/components/answer_editor/AnswerEditor";
import { ReferenceSlideOver } from "@/src/components/ReferenceSlideOver";
import { UpdateQuestionModal } from "./UpdateQuestionModal";
import { ClusterModal } from "./clusters/ClusterModal";
import { NewFilesUploader } from "@/src/components/data_room/file_uploaders/NewFilesUploader";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  UploadContextProvider,
  useUploadContext,
} from "@/src/components/data_room/file_uploaders/useUploadContext";
import { toasts } from "../../components/toasts/toasts";
import {
  getQuestionPriorityText,
  QuestionPriorityIcon,
} from "../../components/QuestionPriorityIcon";
import {
  Breadcrumb,
  Breadcrumbs,
} from "@/src/components/breadcrumbs/Breadcrumbs";
import { links } from "@/src/utils/links";
import { BackButton } from "@/src/components/BackButton";
import { AddMoreModal } from "@/src/components/access_control/AddMoreModal";
import { Avatars } from "@/src/components/Avatars";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { truncateText } from "@/src/utils/truncateText";
import { TripleDotMenu } from "@/src/components/TripleDotMenu";
import { ChartBarIcon } from "@heroicons/react/24/solid";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { getDataRoomIndex } from "@/src/utils/getDataRoomIndex";

// Helper function to render priority text for the button
function renderPriorityText(priority?: any) {
  // Handle the case where priority is null, undefined, "NONE", or QuestionPriority.None
  if (
    !priority ||
    priority === QuestionPriority.None ||
    (typeof priority === "string" && priority.toUpperCase() === "NONE")
  ) {
    return "Set priority";
  }

  return getQuestionPriorityText(priority);
}

// Helper function to generate question URL with file reference
export function getQuestionUrlWithRef(
  dealId: string,
  questionId: string,
  fileId?: string,
) {
  const baseUrl = `/deal/${dealId}/questions/${questionId}`;
  if (fileId) {
    return `${baseUrl}?fileid_popover_ref=${fileId}`;
  }
  return baseUrl;
}

// Component to display DDQL breadcrumbs, either from parent DDQLs or from direct question data
function QuestionBreadcrumbs({
  question,
}: {
  question: QuestionQuery["question"];
}) {
  const { dealId } = useParams<{ dealId: string }>();

  let nodes: Breadcrumb[] = [];

  if (question.ddql) {
    question.ddql.parentDdqls.forEach((ddql) => {
      if (ddql.name === "") {
        return;
      }
      nodes.push({
        link: links.ddqlLink(ddql, dealId),
        text: ddql.name,
      });
    });

    nodes.push({
      link: links.ddqlLink(question.ddql, dealId),
      text: question.ddql.name,
    });
  }

  return <Breadcrumbs breadcrumbs={nodes} pageTitle={""} />;
}

export function DisplayQuestion() {
  const { id: questionId } = useParams<{ id: string }>();
  const { dealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const { subscribeToTopic, unsubscribeFromTopic } = useWebSocket();

  const [openModal, setOpenModal] = useState<"edit" | "delete" | "">("");

  useEffect(() => {
    if (!questionId) return;
    subscribeToTopic(`question:${questionId}`);
    return () => {
      unsubscribeFromTopic(`question:${questionId}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  const trackActivity = useTrackActivityMutation(client);
  const { data, isLoading, error } = useQuestionQuery(
    client,
    { id: questionId },
    {
      refetchInterval(query) {
        if (
          query.state.data &&
          query.state.data.question.suggestedAnswer &&
          query.state.data.question.suggestedAnswer.status === SuggestedAnswerStatus.Complete
        ) {
          return false;
        }

        if (
          query.state.error &&
          typeof query.state.error === "object" &&
          "message" in query.state.error &&
          typeof query.state.error.message === "string" &&
          query.state.error.message.includes("Unauthorized")
        ) {
          return false;
        }

        return 10000;
      },
    },
  );

  const dataRoomQuery = useDataRoomQuery(
    client,
    { dealId: dealId ?? "" },
    {
      enabled: !!dealId,
    },
  );

  // Get parentDdqls from location state if it exists (passed when navigating from DDQLNode)
  const location = useLocation<{
    parentDdqls?: Array<{ id: string; name: string }>;
  }>();
  const parentDdqls = location.state?.parentDdqls;
  const updateQuestion = useUpdateQuestionMutation(client);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!data) {
      return;
    }
    trackActivity.mutate({
      type: DealActivityType.QuestionViewed,
      questionId: questionId,
    });
  }, [questionId, data]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500 font-semibold">
          Something went wrong while loading this question. Please try
          reloading.
        </p>
      </div>
    );
  }

  if (isLoading || !data) {
    return <Loading />;
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex flex-col  md:flex-row items-start md:items-center gap-x-2 bg-white z-1 p-2 w-full border-b border-concrete-100 md:justify-between">
        <div className="">
          <QuestionHeader data={data} />
        </div>
        <div className="mt-2 md:mt-0 flex items-center gap-x-2">
          <PermissionCheck
            input={{
              resource: {
                resourceID: data.question.id,
                resourceType: PermissionsResourceType.Question,
              },
              permissionKey: "questions:edit",
            }}
          >
            <TripleDotMenu>
              <TripleDotMenu.Button
                variant="normal"
                text="Edit question"
                icon={<PencilIcon className="w-4 h-4" />}
                onClick={() => {
                  setOpenModal("edit");
                }}
              />
              <TripleDotMenu.Button
                variant="danger"
                text="Delete question"
                icon={<TrashIcon className="w-4 h-4" />}
                onClick={() => {
                  setOpenModal("delete");
                }}
              />
            </TripleDotMenu>
          </PermissionCheck>
        </div>
      </div>
      <div className="flex flex-col flex-1">
        <QuestionInner
          isLoading={isLoading}
          error={error}
          data={data}
          dataRoomData={dataRoomQuery.data}
        />
      </div>
      <UpdateQuestionModal
        open={openModal === "edit"}
        onClose={() => setOpenModal("")}
        questionId={questionId}
      />
      <DeleteQuestionModal
        open={openModal === "delete"}
        onClose={() => setOpenModal("")}
        question={data.question}
      />
    </div>
  );
}

function DeleteQuestionModal(props: {
  open: boolean;
  onClose: () => void;
  question: QuestionQuery["question"];
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const deleteQuestion = useDeleteQuestionMutation(client);
  const { invalidateAllDdqlQueries } = useInvalidateQueryKeys();
  const { dealId } = useParams<{ dealId: string }>();
  const history = useHistory();

  return (
    <AnimatedModal open={props.open} onClose={props.onClose}>
      <div>
        <div className="flex justify-between items-center">
          <p className="text-persian-990 font-semibold text-md">
            Delete question
          </p>
          <CloseIcon onClose={props.onClose} />
        </div>

        <div className="mt-2 bg-concrete-50 border border-concrete-100 p-2 rounded-md">
          <p className="text-persian-990 font-semibold text-sm">
            {props.question.title}
          </p>
        </div>

        <p className="mt-2 text-persian-990 text-sm">
          Are you sure you want to delete this question? This cannot be undone.
        </p>

        <div className="flex mt-4 justify-end">
          <Button
            onClick={() => {
              deleteQuestion.mutate(
                {
                  input: {
                    id: props.question.id,
                  },
                },
                {
                  onSuccess: () => {
                    props.onClose();
                    toasts.success("Question deleted");
                    invalidateAllDdqlQueries();
                    queryClient.invalidateQueries({
                      queryKey: ["QuestionsView", { dealId: dealId }],
                    });
                    history.goBack();
                  },
                },
              );
            }}
            variant="danger"
            text="Delete question"
            isLoading={deleteQuestion.isPending}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}

function QuestionStatusButton(props: {
  question: QuestionQuery["question"];
  open: boolean;
  onClose: () => void;
  onClick: () => void;
}) {
  const [text, setText] = useState("Set status");
  const [icon, setIcon] = useState<React.ElementType>(CircleDashedIcon);
  const [iconColor, setIconColor] = useState("text-concrete-200");
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateQuestion = useUpdateQuestionMutation(client);

  useEffect(() => {
    if (!props.question.status) {
      return;
    }

    if (props.question.status === QuestionStatus.Approved) {
      setText("Approved");
      setIconColor("text-green-600");
      setIcon(CheckCircleIcon);
      return;
    }

    if (props.question.status === QuestionStatus.Rejected) {
      setText("Rejected");
      setIconColor("text-red-600");
      setIcon(CircleXIcon);
      return;
    }
  }, [props.question.status]);

  function updateQuestionStatus(status: QuestionStatus) {
    if (updateQuestion.isPending) {
      return;
    }

    if (status === QuestionStatus.Approved) {
      setText("Approved");
      setIconColor("text-green-600");
      setIcon(CheckCircleIcon);
    }

    if (status === QuestionStatus.Rejected) {
      setText("Rejected");
      setIconColor("text-red-600");
      setIcon(CircleXIcon);
    }

    if (props.question.status === QuestionStatus.Approved) {
      setText("Approved");
      setIconColor("text-green-600");
      setIcon(CheckCircleIcon);
    }

    if (status === QuestionStatus.Cleared) {
      setText("Set status");
      setIconColor("text-concrete-200");
      setIcon(CircleDashedIcon);
    }

    props.onClose();
    updateQuestion.mutate(
      {
        input: { id: props.question.id, status: status },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["Question", { id: props.question.id }],
          });
          toasts.success("Question status updated");
          props.onClose();
        },
        onError: () => {
          queryClient.invalidateQueries({
            queryKey: ["Question", { id: props.question.id }],
          });
          toasts.error("Failed to update question status");
        },
      },
    );
  }

  return (
    <div>
      <Button
        id="question-status"
        text={text}
        icon={icon}
        iconColor={iconColor}
        variant="neutral/soft"
        onClick={props.onClick}
      />
      <ButtonPopout
        buttonElementId="question-status"
        open={props.open}
        onClose={props.onClose}
        className="w-32"
      >
        <button
          onClick={() => {
            updateQuestionStatus(QuestionStatus.Approved);
          }}
          className="justify-between w-full py-4 px-2 rounded-t-md text-sm text-green-800 hover:bg-concrete-50 flex items-center gap-x-2"
        >
          Approved
          <CheckCircleIcon className="w-4 h-4 text-green-600" />
        </button>
        <button
          onClick={() => {
            updateQuestionStatus(QuestionStatus.Rejected);
          }}
          className="w-full justify-between rounded-b-md py-4 px-2 text-sm text-red-800 hover:bg-concrete-50 flex items-center gap-x-2"
        >
          Rejected
          <CircleXIcon className="w-4 h-4 text-red-600" />
        </button>
        {props.question.status &&
          props.question.status !== QuestionStatus.Cleared ? (
          <button
            onClick={() => {
              updateQuestionStatus(QuestionStatus.Cleared);
            }}
            className="w-full justify-between rounded-b-md py-4 px-2 text-sm text-concrete-800 hover:bg-concrete-50 flex items-center gap-x-2"
          >
            No status
            <CircleXIcon className="w-4 h-4 text-concrete-600" />
          </button>
        ) : null}
      </ButtonPopout>
    </div>
  );
}

function QuestionPriorityButton(props: {
  question: QuestionQuery["question"];
  open: boolean;
  onClose: () => void;
  onClick: () => void;
}) {
  const [text, setText] = useState("Set priority");
  const [iconColor, setIconColor] = useState("text-concrete-200");
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateQuestion = useUpdateQuestionMutation(client);

  useEffect(() => {
    const priority = props.question.priority as any;
    setText(renderPriorityText(priority));

    if (!priority || priority === QuestionPriority.None) {
      setIconColor("text-concrete-200");
      return;
    }

    if (priority === QuestionPriority.Low) {
      setIconColor("text-gray-400");
      return;
    }

    if (priority === QuestionPriority.Medium) {
      setIconColor("text-gray-500");
      return;
    }

    if (priority === QuestionPriority.High) {
      setIconColor("text-gray-700");
      return;
    }

    if (priority === QuestionPriority.Urgent) {
      setIconColor("text-orange-500");
      return;
    }
  }, [props.question.priority]);

  function updateQuestionPriority(priority: QuestionPriority) {
    if (updateQuestion.isPending) {
      return;
    }

    setText(renderPriorityText(priority));

    if (!priority || priority === QuestionPriority.None) {
      setIconColor("text-concrete-200");
    }
    if (priority === QuestionPriority.Low) {
      setIconColor("text-gray-400");
    }
    if (priority === QuestionPriority.Medium) {
      setIconColor("text-gray-500");
    }
    if (priority === QuestionPriority.High) {
      setIconColor("text-gray-700");
    }
    if (priority === QuestionPriority.Urgent) {
      setIconColor("text-orange-500");
    }

    props.onClose();
    updateQuestion.mutate(
      {
        input: { id: props.question.id, priority: priority },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["Question", { id: props.question.id }],
          });
          toasts.success("Question priority updated");
          props.onClose();
        },
        onError: () => {
          queryClient.invalidateQueries({
            queryKey: ["Question", { id: props.question.id }],
          });
          toasts.error("Failed to update question priority");
        },
      },
    );
  }

  return (
    <div>
      <Button
        id="question-priority"
        text={text}
        icon={ChartBarIcon}
        iconColor={iconColor}
        variant="neutral/soft"
        onClick={props.onClick}
      />
      <ButtonPopout
        buttonElementId="question-priority"
        open={props.open}
        onClose={props.onClose}
        className="w-40"
      >
        <button
          onClick={() => updateQuestionPriority(QuestionPriority.None)}
          className="justify-between w-full py-3 px-2 rounded-t-md text-sm text-gray-700 hover:bg-concrete-50 flex items-center gap-x-2"
        >
          <div className="flex items-center gap-x-2">
            <QuestionPriorityIcon priority={QuestionPriority.None} />
            <span>None</span>
          </div>
        </button>
        <button
          onClick={() => updateQuestionPriority(QuestionPriority.Low)}
          className="justify-between w-full py-3 px-2 text-sm text-gray-700 hover:bg-concrete-50 flex items-center gap-x-2"
        >
          <div className="flex items-center gap-x-2">
            <QuestionPriorityIcon priority={QuestionPriority.Low} />
            <span>Low</span>
          </div>
        </button>
        <button
          onClick={() => updateQuestionPriority(QuestionPriority.Medium)}
          className="justify-between w-full py-3 px-2 text-sm text-gray-700 hover:bg-concrete-50 flex items-center gap-x-2"
        >
          <div className="flex items-center gap-x-2">
            <QuestionPriorityIcon priority={QuestionPriority.Medium} />
            <span>Medium</span>
          </div>
        </button>
        <button
          onClick={() => updateQuestionPriority(QuestionPriority.High)}
          className="justify-between w-full py-3 px-2 text-sm text-gray-700 hover:bg-concrete-50 flex items-center gap-x-2"
        >
          <div className="flex items-center gap-x-2">
            <QuestionPriorityIcon priority={QuestionPriority.High} />
            <span>High</span>
          </div>
        </button>
        <button
          onClick={() => updateQuestionPriority(QuestionPriority.Urgent)}
          className="justify-between w-full py-3 px-2 rounded-b-md text-sm text-orange-800 hover:bg-concrete-50 flex items-center gap-x-2"
        >
          <div className="flex items-center gap-x-2">
            <QuestionPriorityIcon priority={QuestionPriority.Urgent} />
            <span>Urgent</span>
          </div>
        </button>
      </ButtonPopout>
    </div>
  );
}

function QuestionHeader(props: { data: QuestionQuery }) {
  const client = useGqlClient();
  const questionAccessQuery = useQuestionAccessQuery(client, {
    id: props.data.question.id,
  });
  const queryClient = useQueryClient();
  const { dealId } = useParams<{ dealId: string }>();

  const [openModal, setOpenModal] = useState<"" | "manage_access">("");

  const accessControlAccounts = [
    ...(questionAccessQuery.data
      ? questionAccessQuery.data.question.accessControlDealAccounts.map(
        (da) => da.dealAccount.account,
      )
      : []),
    ...(questionAccessQuery.data
      ? questionAccessQuery.data.question.accessControlDealGroups.flatMap(
        (acdg) => acdg.accounts.map((da) => da.dealAccount.account),
      )
      : []),
  ];

  return (
    <div className="flex items-center gap-x-2 px-2">
      <BackButton size="xs" />
      <QuestionBreadcrumbs question={props.data.question} />
      <div className="flex items-center gap-x-2">
        <p className="text-md font-semibold text-persian-990">
          {truncateText(
            props.data.question.title,
            props.data.question.ddql ? 32 : 96,
          )}
        </p>
        {!props.data.question.ddql ? (
          <>
            <PermissionCheck
              input={{
                permissionKey: "questions:manage_access",
                resource: {
                  resourceID: props.data.question.id,
                  resourceType: PermissionsResourceType.Question,
                },
              }}
            >
              <div className="flex items-center gap-x-2 mt-0.5">
                <Avatars
                  accounts={accessControlAccounts}
                  max={6}
                  showAdd
                  size="s"
                  onClick={() => {
                    setOpenModal("manage_access");
                  }}
                />
              </div>
            </PermissionCheck>
          </>
        ) : null}
      </div>

      <AddMoreModal
        open={openModal === "manage_access"}
        onClose={() => {
          setOpenModal("");
          queryClient.invalidateQueries({
            queryKey: ["QuestionAccessControl", { id: props.data.question.id }],
          });
        }}
        dealId={dealId ?? ""}
        access={
          (questionAccessQuery.data?.question
            .accessControlDealGroups as AccessControlDealGroup[]) ?? []
        }
        resourceId={props.data.question.id}
        resourceType={PermissionsResourceType.Question}
      />
    </div>
  );
}

function QuestionInner(props: {
  isLoading: boolean;
  data: QuestionQuery;
  dataRoomData?: DataRoomQuery;
  error: any;
}) {
  if (props.error) {
    if (props.error.message && props.error.message.includes("Unauthorized")) {
      return (
        <div className="flex-1 justify-center items-center">
          <div className="flex h-full flex-1 justify-center items-center">
            <p>You don't have access to this question content</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 justify-center items-center">
        <p>Something went wrong</p>
      </div>
    );
  }

  if (props.isLoading || !props.data) {
    return (
      <div className="flex-1 justify-center items-center">
        <Loading />
      </div>
    );
  }

  return (
    <QuestionContent
      question={props.data.question}
      dataRoomData={props.dataRoomData}
    />
  );
}

function QuestionContent(props: {
  question: QuestionQuery["question"];
  dataRoomData?: any;
}) {
  const [answer, setAnswer] = useState("");
  const [answerFiles, setAnswerFiles] = useState<AnswerReference[]>([]);
  const [postToSimilar, setPostToSimilar] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const account = useSelector(authSelectors.account);
  const location = useLocation();

  const [openModal, setOpenModal] = useState<
    "add-document" | "set-status" | "set-priority" | ""
  >("");

  const [selectedReference, setSelectedReference] = useState<
    AnswerReference | undefined
  >(undefined);

  const clearRefFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("fileid_popover_ref")) {
      searchParams.delete("fileid_popover_ref");
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      window.history.replaceState({}, "", newUrl);
    }
  };

  const setSelectedReferenceWithUrl = (
    reference: AnswerReference | undefined,
  ) => {
    setSelectedReference(reference);
    if (!reference) {
      clearRefFromUrl();
    }
  };

  // Get permissions from data room
  const defaultPermissions =
    props.dataRoomData?.deal?.dataRoom?.permissions || [];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refParam = searchParams.get("fileid_popover_ref");

    if (refParam && !selectedReference) {
      let foundReference: AnswerReference | undefined;

      if (props.question.dataRoomFile?.id === refParam) {
        foundReference = {
          file: props.question.dataRoomFile as DataRoomFile,
          pageIndex: 0,
          rectsOnPage: [],
          quote: "",
          topReference: true,
        };
      }

      if (!foundReference && props.question.answers) {
        for (const answer of props.question.answers) {
          if (answer?.answerFiles) {
            const matchingFile = answer.answerFiles.find(
              (answerFile) => answerFile.file.id === refParam,
            );
            if (matchingFile) {
              foundReference = {
                file: matchingFile.file as DataRoomFile,
                pageIndex: matchingFile.pageIndex ?? 0,
                rectsOnPage: matchingFile.rectsOnPage ?? [],
                quote: "",
                topReference: true,
              };
              break;
            }
          }
        }
      }

      if (foundReference) {
        setSelectedReference(foundReference);
      }
    }
  }, [props.question, location.search]);

  const addedFileKeys = useMemo(
    () =>
      new Set(
        answerFiles.map((f) =>
          fileRefKey({
            fileId: f.file.id,
            pageIndex: f.pageIndex,
            rectsOnPage: f.rectsOnPage,
          }),
        ),
      ),
    [answerFiles],
  );

  const handleSuggestionFileClick = useCallback(
    (data: InlineFileClickData) => {
      setSelectedReference({
        file: {
          id: data.fileId,
          name: data.fileName,
          fileType: data.fileType,
          folderId: data.folderId,
        } as DataRoomFile,
        pageIndex: data.pageIndex ?? 0,
        quote: "",
        rectsOnPage: data.rectsOnPage ?? [],
        topReference: false,
      });
    },
    [],
  );

  const handleAddSuggestionFile = useCallback(
    (data: InlineFileClickData) => {
      const key = fileRefKey(data);
      setAnswerFiles((prev) => {
        const existingKeys = new Set(
          prev.map((f) =>
            fileRefKey({
              fileId: f.file.id,
              pageIndex: f.pageIndex,
              rectsOnPage: f.rectsOnPage,
            }),
          ),
        );
        if (existingKeys.has(key)) return prev;
        return [
          ...prev,
          {
            file: {
              id: data.fileId,
              name: data.fileName,
              fileType: data.fileType,
              folderId: data.folderId,
            } as DataRoomFile,
            pageIndex: data.pageIndex ?? 0,
            quote: "",
            rectsOnPage: data.rectsOnPage ?? [],
            topReference: false,
          },
        ];
      });
      toasts.success("File added to answer");
    },
    [],
  );

  const handleUseAnswer = useCallback(
    (text: string, files: InlineFileClickData[]) => {
      setAnswer(text);
      setAnswerFiles((prev) => {
        const existingKeys = new Set(
          prev.map((f) =>
            fileRefKey({
              fileId: f.file.id,
              pageIndex: f.pageIndex,
              rectsOnPage: f.rectsOnPage,
            }),
          ),
        );
        const newFiles = files
          .filter((f) => !existingKeys.has(fileRefKey(f)))
          .map((f) => ({
            file: {
              id: f.fileId,
              name: f.fileName,
              fileType: f.fileType,
              folderId: f.folderId,
            } as DataRoomFile,
            pageIndex: f.pageIndex ?? 0,
            quote: "",
            rectsOnPage: f.rectsOnPage ?? [],
            topReference: false,
          }));
        return [...prev, ...newFiles];
      });
      toasts.success("Suggestion added to answer");
    },
    [],
  );

  if (!account) {
    return null;
  }

  return (
    <div className="px-2 flex-1 ">
      <div className="flex h-full ">
        <div className="w-full px-9 py-2 pb-12 border-r border-concrete-100 max-h-[calc(100vh-180px)] overflow-y-scroll no-scrollbar">
          <div className=" mt-2">
            {props.question.createdBy ? (
              <div className="mt-2 flex items-center gap-x-2">
                <Avatar size="s" account={props.question.createdBy} />
                <p className="text-concrete-950 text-sm">
                  {props.question.createdBy.name}
                </p>
              </div>
            ) : null}
            <div className="flex-1 mt-4">
              <p className=" text-concrete-950 text-md font-semibold">
                <Markdown>{props.question.title}</Markdown>
              </p>
              <p className="whitespace-pre-wrap text-sm text-concrete-950 mt-2">
                <Markdown>{props.question.body}</Markdown>
              </p>
              {props.question.dataRoomFile ? (
                <Pills>
                  <FilePill
                    id={props.question.dataRoomFile.id}
                    name={props.question.dataRoomFile.name}
                    type={props.question.dataRoomFile.fileType}
                    useSlideOver={true}
                    onClick={() => {
                      setSelectedReference({
                        file: props.question.dataRoomFile as DataRoomFile,
                        pageIndex: 0,
                        rectsOnPage: [],
                        quote: "",
                        topReference: true,
                      });
                    }}
                  />
                </Pills>
              ) : null}
            </div>
          </div>

          {!props.question.answers || props.question.answers.length === 0 ? (
            <div className="my-4 rounded-md px-4 py-3 border border-concrete-100 bg-concrete-50">
              <p className="font-semibold text-concrete-950 text-md">
                No answers
              </p>
            </div>
          ) : null}

          <QuestionAnswer
            question={props.question}
            setAnswer={setAnswer}
            setAnswerFiles={setAnswerFiles}
            setSelectedReference={setSelectedReference}
            selectedReference={selectedReference}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            dataRoomPermissions={defaultPermissions}
          />
          <PermissionCheck
            input={{
              permissionKey: "questions:answer",
              resource: {
                resourceID: props.question.id,
                resourceType: PermissionsResourceType.Question,
              },
            }}
          >
            <SimilarQuestionsPanel question={props.question} />
            <AddQuestionAnswer
              question={props.question}
              answer={answer}
              answerFiles={answerFiles}
              setAnswer={setAnswer}
              setAnswerFiles={setAnswerFiles}
              setSelectedReference={setSelectedReference}
              selectedReference={selectedReference}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              dataRoomPermissions={defaultPermissions}
              postToSimilar={postToSimilar}
              setPostToSimilar={setPostToSimilar}
            />
          </PermissionCheck>

          <PermissionCheck
            input={{
              roleType: DealRoleType.Internal,
              permissionKey: "questions:answer",
              resource: {
                resourceID: props.question.id,
                resourceType: PermissionsResourceType.Question,
              },
            }}
          >
            <SuggestedAnswerAgent
              question={props.question}
              onFileClick={handleSuggestionFileClick}
              onAddFile={handleAddSuggestionFile}
              onUseAnswer={handleUseAnswer}
              addedFileKeys={addedFileKeys}
            />
          </PermissionCheck>

          <ReferenceSlideOver
            reference={
              selectedReference
                ? {
                  file: selectedReference.file as DataRoomFile,
                  fileVersionId:
                    selectedReference?.file?.currentLiveVersion?.id ?? "",
                  folderId: selectedReference?.file?.parentFolder?.id ?? "",
                  pageIndex: selectedReference.pageIndex ?? 0,
                  quote: selectedReference.quote ?? "",
                  rectsOnPage: selectedReference.rectsOnPage ?? [],
                }
                : undefined
            }
            open={!!selectedReference}
            onClose={() => setSelectedReferenceWithUrl(undefined)}
          />

          <div className="mt-4">
            <PermissionCheck
              input={{
                permissionKey: "questions:manage_access",
                resource: {
                  resourceID: props.question.id,
                  resourceType: PermissionsResourceType.Question,
                },
              }}
            >
              <p className="text-md text-gray-700 font-semibold">Activity</p>
              <Activity question={props.question} />
            </PermissionCheck>
          </div>
        </div>

        <div className="w-96 h-full ">
          <div className="grid-cols-2 hidden lg:grid px-3 py-4 items-center gap-y-4">
            <div>
              <p className="font-semibold text-concrete-950 text-sm">Status</p>
            </div>
            <div className="flex justify-end">
              <QuestionStatusButton
                question={props.question}
                open={openModal === "set-status"}
                onClose={() => setOpenModal("")}
                onClick={() => {
                  if (openModal === "set-status") {
                    setOpenModal("");
                    return;
                  }

                  setOpenModal("set-status");
                }}
              />
            </div>

            <div>
              <p className="font-semibold text-concrete-950 text-sm">
                Priority
              </p>
            </div>
            <div className="flex justify-end">
              <QuestionPriorityButton
                question={props.question}
                open={openModal === "set-priority"}
                onClose={() => setOpenModal("")}
                onClick={() => {
                  if (openModal === "set-priority") {
                    setOpenModal("");
                    return;
                  }
                  setOpenModal("set-priority");
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddQuestionAnswer(props: {
  question: QuestionQuery["question"];
  answer: string;
  setAnswer: (answer: string) => void;
  answerFiles: AnswerReference[];
  setAnswerFiles: (files: AnswerReference[]) => void;
  setSelectedReference: (reference: AnswerReference | undefined) => void;
  selectedReference: AnswerReference | undefined;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  dataRoomPermissions: DataRoomPermission[];
  postToSimilar: boolean;
  setPostToSimilar: (v: boolean) => void;
}) {
  const { dealId } = useParams<{ dealId: string }>();

  const client = useGqlClient();
  const createQuestionAnswer = useCreateQuestionAnswerMutation(client);
  const queryClient = useQueryClient();
  const account = useSelector(authSelectors.account);

  const [error, setError] = useState<string | null>(null);

  const otherUnansweredMembers = (
    props.question.cluster?.members ?? []
  ).filter(
    (m) => m.question.id !== props.question.id && !m.question.answeredAt,
  );

  const [openModal, setOpenModal] = useState<
    "add-document" | "upload-document" | ""
  >("");

  // Use the first permission as default if available
  const defaultPermission =
    props.dataRoomPermissions.length > 0 ? [props.dataRoomPermissions[0]] : [];

  if (!account) {
    return null;
  }

  const resourceId = props.question.ddql
    ? props.question.ddql.id
    : props.question.id;
  const resourceType = props.question.ddql
    ? PermissionsResourceType.Ddql
    : PermissionsResourceType.Question;

  return (
    <div>
      <PermissionCheck
        input={{
          resource: {
            resourceID: resourceId,
            resourceType: resourceType,
          },
          permissionKey: "questions:answer",
        }}
      >
        <div className="">
          <div className="flex gap-x-3 ">
            <div className="flex space-x-2 w-full">
              <form
                onSubmit={(e) => {
                  e.preventDefault();

                  if (!props.answer.trim() && props.answerFiles.length === 0) {
                    setError("Your answer cannot be empty");
                    return;
                  }

                  setError(null);

                  const answerInput = {
                    answer: props.answer,
                    dataRoomFileIds: props.answerFiles.map((f) => f.file.id),
                    answerFiles: props.answerFiles.map((f) => ({
                      dataRoomFileID: f.file.id,
                      pageIndex: f.pageIndex,
                      rectsOnPage: f.rectsOnPage,
                    })),
                  };

                  const postIds = [
                    props.question.id,
                    ...(props.postToSimilar
                      ? otherUnansweredMembers.map((m) => m.question.id)
                      : []),
                  ];

                  Promise.all(
                    postIds.map((questionId) =>
                      createQuestionAnswer.mutateAsync({
                        questionId,
                        ...answerInput,
                      }),
                    ),
                  ).then(() => {
                    queryClient.invalidateQueries({
                      queryKey: ["Question", { id: props.question.id }],
                    });
                    props.setAnswer("");
                    props.setAnswerFiles([]);
                    props.setPostToSimilar(false);
                    setError(null);
                  }).catch(() => {
                    setError("Something went wrong posting your answer");
                  });
                }}
                className="relative flex-auto"
              >
                <div className="group rounded-lg focus-within:bg-white shadow-sm ring-1 ring-inset ring-concrete-100 focus-within:ring-1 focus-within:ring-persian-600">
                  <AnswerEditor
                    content={props.answer}
                    onChange={(md) => {
                      props.setAnswer(md);
                      if (error) setError(null);
                    }}
                    placeholder="Add an answer..."
                  />
                  {error && (
                    <div className="px-4 pb-2 text-red-500 text-sm">
                      {error}
                    </div>
                  )}

                  <div className="">
                    <div className="mx-2 h-px bg-concrete-100" />
                    <div className="p-2 flex flex-col sm:flex-row sm:justify-between">
                      <div className="flex items-center flex-wrap gap-2 sm:max-w-[70%]">
                        {props.answerFiles.map((file) => (
                          <div key={file.file.id}>
                            <FilePill
                              id={file.file.id}
                              name={file.file.name}
                              type={file.file.fileType}
                              pageIndex={file.pageIndex ?? undefined}
                              rectsOnPage={file.rectsOnPage}
                              useSlideOver={true}
                              showDetailsCard={false}
                              onClick={() => {
                                props.setSelectedReference({
                                  file: file.file as DataRoomFile,
                                  pageIndex: file.pageIndex ?? 0,
                                  rectsOnPage: file.rectsOnPage ?? [],
                                  quote: "",
                                  topReference: true,
                                });
                              }}
                              onRemove={() => {
                                props.setAnswerFiles(
                                  props.answerFiles.filter(
                                    (f) => f.file.id !== file.file.id,
                                  ),
                                );
                              }}
                            />
                          </div>
                        ))}

                        <Menu as="div" className="relative inline-block">
                          <Menu.Button className="relative inline-flex items-center gap-x-2 rounded-md text-sm font-semibold ring-1 ring-inset shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-white text-gray-600 ring-gray-300 hover:bg-gray-50 focus-visible:outline-gray-600 px-2 py-1">
                            Add document
                            <div className="h-5 mx-1 border-l border-gray-300"></div>
                            <ChevronDownIcon
                              className="w-5 h-5"
                              aria-hidden="true"
                            />
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute left-0 z-10 mt-2 w-72 origin-top-left rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    className={classNames(
                                      active ? "bg-gray-100" : "",
                                      "px-4 py-2 text-sm text-gray-700 w-full text-left flex items-center",
                                    )}
                                    onClick={() =>
                                      setOpenModal("upload-document")
                                    }
                                  >
                                    <UploadIcon className="w-5 h-5 text-gray-600/80 mr-3" />
                                    Upload new documents
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    className={classNames(
                                      active ? "bg-gray-100" : "",
                                      "px-4 py-2 text-sm text-gray-700 w-full text-left flex items-center",
                                    )}
                                    onClick={() => setOpenModal("add-document")}
                                  >
                                    <FolderPlusIcon className="w-5 h-5 text-gray-600/80 mr-3" />
                                    Add from Data Room
                                  </button>
                                )}
                              </Menu.Item>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </div>

                      <div className="flex items-center gap-x-3 mt-4 sm:mt-0 self-end">
                        {otherUnansweredMembers.length > 0 && (
                          <label className="flex items-center gap-x-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={props.postToSimilar}
                              onChange={(e) =>
                                props.setPostToSimilar(e.target.checked)
                              }
                              className="rounded border-concrete-300 text-persian-600 focus:ring-persian-500"
                            />
                            <span className="text-xs text-concrete-500">
                              Also post to {otherUnansweredMembers.length}{" "}
                              similar question
                              {otherUnansweredMembers.length !== 1 ? "s" : ""}
                            </span>
                          </label>
                        )}
                        <Button
                          isLoading={createQuestionAnswer.isPending}
                          loadingText="Answering..."
                          size="s"
                          variant="positive"
                          type="submit"
                          text={
                            props.postToSimilar &&
                              otherUnansweredMembers.length > 0
                              ? `Answer all ${otherUnansweredMembers.length + 1}`
                              : "Answer"
                          }
                          icon={ArrowUpIcon}
                          isDisabled={
                            props.answer.trim() === "" &&
                            props.answerFiles.length === 0
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
              <AddFileModal
                open={openModal === "add-document"}
                onClose={() => {
                  setOpenModal("");
                }}
                onFilesSelected={(files) => {
                  const filesToAdd = files
                    .filter(
                      (file) =>
                        !props.answerFiles.find(
                          (f) => f.file.id === file.file.id,
                        ),
                    )
                    .map((f) => ({
                      file: f.file as DataRoomFile,
                      pageIndex: 0,
                      rectsOnPage: [],
                      quote: "",
                      topReference: true,
                    }));

                  props.setAnswerFiles([...props.answerFiles, ...filesToAdd]);
                  if (error) {
                    setError(null);
                  }
                }}
              />
              {openModal === "upload-document" && (
                <QuestionFilesUploader
                  dealId={dealId}
                  question={props.question}
                  permissions={defaultPermission}
                  answerFiles={props.answerFiles}
                  setAnswerFiles={props.setAnswerFiles}
                  onClose={() => setOpenModal("")}
                />
              )}
            </div>
          </div>
        </div>
      </PermissionCheck>
    </div>
  );
}

function QuestionFilesUploader(props: {
  dealId: string;
  question: QuestionQuery["question"];
  permissions: DataRoomPermission[];
  answerFiles: AnswerReference[];
  setAnswerFiles: (files: AnswerReference[]) => void;
  onClose: () => void;
}) {
  return (
    <UploadContextProvider
      navigationScopePath={`/deal/${props.dealId}/questions/${props.question.id}`}
    >
      <QuestionFilesUploaderContent {...props} />
    </UploadContextProvider>
  );
}

function QuestionFilesUploaderContent(props: {
  question: QuestionQuery["question"];
  permissions: DataRoomPermission[];
  answerFiles: AnswerReference[];
  setAnswerFiles: (files: AnswerReference[]) => void;
  onClose: () => void;
}) {
  const client = useGqlClient();
  const { uploadFiles } = useUploadContext();
  const questionQuery = useQuestionQuery(client, { id: props.question.id });

  useEffect(() => {
    const newAnswerReferences = uploadFiles
      .filter(
        (uploadFile) =>
          uploadFile.status === "complete" &&
          uploadFile.createdDataRoomFileId &&
          !props.answerFiles.some(
            (answerFile) =>
              answerFile.file.id === uploadFile.createdDataRoomFileId,
          ),
      )
      .map((uploadFile) => ({
        file: {
          id: uploadFile.createdDataRoomFileId as string,
          name: uploadFile.name,
          fileType: uploadFile.file.type as FileType,
          currentLiveVersion: { id: "" },
        } as DataRoomFile,
        pageIndex: 0,
        rectsOnPage: [],
        quote: "",
        topReference: true,
      }));

    if (newAnswerReferences.length > 0) {
      props.setAnswerFiles([...props.answerFiles, ...newAnswerReferences]);
    }
  }, [uploadFiles, props.answerFiles, props.setAnswerFiles]);

  const uploadedQuestion = questionQuery.data?.question;

  return (
    <NewFilesUploader
      open
      onClose={props.onClose}
      permissions={props.permissions}
      keepOpenDuringUpload
      folder={
        uploadedQuestion?.dataRoomFolder
          ? (uploadedQuestion.dataRoomFolder as DataRoomFolder)
          : undefined
      }
      question={
        uploadedQuestion && !uploadedQuestion.dataRoomFolder
          ? uploadedQuestion
          : undefined
      }
      isLoading={questionQuery.isLoading}
      error={questionQuery.error}
    />
  );
}

function QuestionAnswer(props: {
  question: QuestionQuery["question"];
  setAnswer: (answer: string) => void;
  setAnswerFiles: (files: AnswerReference[]) => void;
  setSelectedReference: (reference: AnswerReference | undefined) => void;
  selectedReference: AnswerReference | undefined;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  dataRoomPermissions: DataRoomPermission[];
}) {
  const { dealId } = useParams<{ dealId: string }>();

  const client = useGqlClient();
  const account = useSelector(authSelectors.account);

  const dataRoomQuery = useDataRoomQuery(client, {
    dealId: dealId,
  });

  const folder = useDataRoomFolderQuery(
    client,
    {
      id: dataRoomQuery.data?.deal?.dataRoom?.folder.id ?? "",
    },
    {
      enabled:
        dataRoomQuery.data &&
        dataRoomQuery.data.deal &&
        dataRoomQuery.data.deal.dataRoom &&
        dataRoomQuery.data.deal.dataRoom.folder &&
        dataRoomQuery.data.deal.dataRoom.folder.id !== "",
    },
  );

  if (!folder.data || !dataRoomQuery.data) {
    return (
      <div className="h-80 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!account) {
    return null;
  }

  const resourceId = props.question.ddql
    ? props.question.ddql.id
    : props.question.id;
  const resourceType = props.question.ddql
    ? PermissionsResourceType.Ddql
    : PermissionsResourceType.Question;

  // --- Displaying Answers ---
  if (props.question.answers && props.question.answers.length !== 0) {
    const sortedAnswers = [...props.question.answers] // Create a shallow copy
      .filter((answer): answer is NonNullable<typeof answer> => answer !== null) // Type guard for filtering nulls
      .sort((a, b) => a.createdAt - b.createdAt); // Sort by creation time ascending

    return (
      <div className="bg-green-50 border-green-200 border p-2 rounded-md mt-6">
        <div>
          <p className="text-concrete-950 font-semibold text-md">
            {props.question.answers.length}{" "}
            {props.question.answers.length > 1 ? "Answers" : "Answer"}
          </p>
        </div>
        <div className="mt-2">
          {sortedAnswers.map((answer) => (
            <div key={answer.id} className="mt-4 flex flex-col gap-y-3 group">
              {" "}
              {/* Add group class back here */}
              {/* Answer Card */}
              <div className="flex space-x-2 w-full">
                <div className="flex-1 w-full">
                  <div className="bg-white border border-concrete-100 rounded-md p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-x-2">
                        <Avatar account={answer.answeredBy} size="s" />
                        <p className="font-semibold text-concrete-950 text-sm">
                          Answered by {answer.answeredBy?.name}
                        </p>
                        <p className="text-xs text-concrete-600 ml-2">
                          {formatDistanceToNow(fromUnixTime(answer.createdAt), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>

                      {answer && answer.answer ? (
                        <CopyAnswerButton text={answer.answer} />
                      ) : null}
                    </div>
                    <div className="text-sm text-concrete-950 mt-2 py-2">
                      <FileRefClickProvider
                        value={(data: FileRefClickData) => {
                          props.setSelectedReference({
                            file: {
                              id: data.fileId,
                              name: data.fileName,
                              fileType: data.fileType,
                            } as DataRoomFile,
                            pageIndex: data.pageIndex ?? 0,
                            rectsOnPage: data.rectsOnPage ?? [],
                            quote: "",
                            topReference: true,
                          });
                        }}
                      >
                        <AnswerViewer content={answer.answer} />
                      </FileRefClickProvider>
                    </div>
                    {/* Display attached files (AnswerReferences) */}
                    {answer.answerFiles && answer.answerFiles.length > 0 && (
                      <div className="pt-1 mb-1">
                        <div className="flex items-center flex-wrap gap-2">
                          <Pills>
                            {answer.answerFiles.map((fileRef) => (
                              <FilePill
                                key={
                                  fileRef.file.id + (fileRef.pageIndex || "")
                                } // Ensure unique key if same file referenced multiple times
                                id={fileRef.file.id}
                                name={fileRef.file.name}
                                type={fileRef.file.fileType}
                                showDetailsCard={false}
                                pageIndex={fileRef.pageIndex ?? undefined}
                                rectsOnPage={fileRef.rectsOnPage}
                                dataRoomIndex={getDataRoomIndex(
                                  fileRef.file.index?.sortIndex,
                                  fileRef.file.parentFolders,
                                  "file",
                                )}
                                // checkFileAccessAccountId={answer.answeredBy?.id}
                                useSlideOver={true}
                                onClick={() => {
                                  props.setSelectedReference({
                                    file: fileRef.file as DataRoomFile,
                                    pageIndex: fileRef.pageIndex ?? 0,
                                    rectsOnPage: fileRef.rectsOnPage ?? [],
                                    quote: "", // Quote not stored directly on answerFile, maybe fetch later?
                                    topReference: true,
                                  });
                                }}
                              />
                            ))}
                          </Pills>
                          <div className="">
                            <CopyFilesButton
                              files={answer.answerFiles}
                              dealId={dealId}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* --- Render Thread Messages --- */}
                    {answer.threadMessages &&
                      answer.threadMessages.length > 0 && (
                        <div className="pt-3 mt-3 border-t border-gray-200 space-y-3">
                          {answer.threadMessages
                            .filter((msg) => msg !== null) // Filter out potential nulls
                            .sort((a, b) => a.createdAt - b.createdAt) // Sort messages by time
                            .map((message) => (
                              <div key={message.id} className="flex gap-x-3">
                                <div className="flex-1 bg-gray-50 rounded-md p-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs font-semibold text-gray-700">
                                      {message.createdBy.name}
                                    </p>
                                    <p className="text-xs text-gray-500/80">
                                      {formatDistanceToNow(
                                        fromUnixTime(message.createdAt),
                                        { addSuffix: true },
                                      )}
                                    </p>
                                  </div>
                                  <p className="whitespace-pre-wrap text-sm text-gray-700">
                                    {message.answer}
                                  </p>
                                  {/* Display attached files for the message */}
                                  {message.dataRoomFiles &&
                                    message.dataRoomFiles.length > 0 && (
                                      <div className="mt-2 pt-2 border-t border-gray-200/60">
                                        <Pills>
                                          {message.dataRoomFiles.map((file) => (
                                            <FilePill
                                              key={file.id}
                                              id={file.id}
                                              name={file.name}
                                              type={file.fileType}
                                              // checkFileAccessAccountId={
                                              //   message.createdBy.id
                                              // }
                                              // noAccessMessage={`${message.createdBy.name} may not have access to this file`}
                                              useSlideOver={true}
                                              onClick={() => {
                                                // Need to adapt setSelectedReference to handle plain DataRoomFile
                                                props.setSelectedReference({
                                                  file: file as DataRoomFile,
                                                  pageIndex: 0,
                                                  rectsOnPage: [],
                                                  quote: "",
                                                  topReference: true,
                                                });
                                              }}
                                            />
                                          ))}
                                        </Pills>
                                      </div>
                                    )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}

                    <div
                      className={`pt-3 mt-3 overflow-hidden transition-all duration-300 ease-in-out`}
                    >
                      <PermissionCheck
                        input={{
                          permissionKey: "questions:answer",
                          resource: {
                            resourceID: props.question.id,
                            resourceType: PermissionsResourceType.Question,
                          },
                        }}
                      >
                        {answer && (
                          <AnswerThreadCommentForm
                            questionAnswerId={answer.id}
                            questionId={props.question.id}
                            createdBy={props.question.createdBy}
                          />
                        )}
                      </PermissionCheck>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null; // Return null if there are no answers (or handle differently if needed)
}

function SimilarQuestionsPanel(props: {
  question: QuestionQuery["question"];
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { cluster } = props.question;
  if (!cluster) return null;

  const otherMembers = cluster.members.filter(
    (m) => m.question.id !== props.question.id,
  );
  if (otherMembers.length === 0) return null;

  const answeredCount = otherMembers.filter(
    (m) => !!m.question.answeredAt,
  ).length;

  return (
    <div className="mt-6 px-6">
      <div className="w-full max-w-3xl ml-4 px-4 py-1.5 flex items-center justify-between bg-concrete-50 rounded-t-lg border border-concrete-100">
        <div className="flex items-center gap-x-2">
          <SparklesIcon className="w-3.5 h-3.5 text-persian-500 shrink-0" />
          <p className="text-sm font-semibold text-persian-950">
            {otherMembers.length} similar question
            {otherMembers.length !== 1 ? "s" : ""}
            {answeredCount > 0 && (
              <span className="ml-1.5 text-persian-900 font-normal">
                — {answeredCount} answered
              </span>
            )}
          </p>
        </div>
        <Button
          variant="secondary"
          size="xs"
          text="View questions"
          onClick={() => setModalOpen(true)}
        />
      </div>

      <ClusterModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["Question", { id: props.question.id }],
          });
        }}
        clusterId={cluster.id}
      />
    </div>
  );
}


function Activity(props: { question: QuestionQuery["question"] }) {
  const account = useSelector(authSelectors.account);
  const [isFocused, setIsFocused] = useState(false);

  const queryClient = useQueryClient();
  const client = useGqlClient();

  const createComment = useCreateCommentMutation(client);

  const [files, setFiles] = useState<DataRoomFile[]>([]);
  const [comment, setComment] = useState("");

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [openModal, setOpenModal] = useState<"add-document" | "">("");

  const createCommentRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (createCommentRef.current) {
        createCommentRef.current.click();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function clearForm() {
    setComment("");
    setFiles([]);
  }

  function onSubmit() {
    if (!comment) {
      return;
    }

    createComment.mutate(
      {
        input: {
          questionId: props.question.id,
          comment,
          files: files.map((f) => f.id),
          dataRoomFileId: "",
          dataRoomFolderId: "",
          dataRoomId: "",
          dataRoomFileErrorId: "",
        },
      },
      {
        onSuccess: () => {
          clearForm();
          queryClient.invalidateQueries({
            queryKey: ["Question", { id: props.question.id }],
          });
        },
      },
    );
  }

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (
        !document.activeElement ||
        !document.activeElement.closest("#comment_form")
      ) {
        setIsFocused(false);
      }
    }, 0);
  };

  if (!account) {
    return null;
  }

  return (
    <>
      {props.question.activity.length > 0 ? (
        <ul role="list">
          <TransitionGroup className="space-y-6 mt-2">
            {props.question.activity
              .map((activity, i) => {
                return (
                  <CSSTransition
                    key={activity.id}
                    timeout={300} // This controls the duration of the animation
                    classNames="fade-slide-down"
                    onEnter={(node: any) => node && node.offsetHeight} // Trigger reflow to enable animation, with null check
                  >
                    <ActivityItem
                      key={activity.id}
                      activity={activity}
                      isFirst={i === props.question.activity.length - 1}
                      isLast={i === 0}
                    />
                  </CSSTransition>
                );
              })
              .reverse()}
          </TransitionGroup>
        </ul>
      ) : (
        <p className="text-gray-500 text-sm">No activity yet</p>
      )}

      {/* New comment form */}
      <div className="mt-6 flex gap-x-3 " onBlur={handleBlur}>
        <form
          id="comment_form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="relative flex-auto"
          autoFocus={false}
        >
          <div
            className={`overflow-hidden rounded-lg ${isFocused ? "pb-12" : "pb-1"
              } shadow-sm ring-1 ring-inset transition-all ease-in-out duration-300 ring-concrete-100 focus-within:bg-white focus-within:ring-persian-600 focus:ring-2`}
          >
            <label htmlFor="comment" className="sr-only">
              Add your comment
            </label>
            <textarea
              ref={textAreaRef}
              rows={isFocused ? 2 : 1}
              name="comment"
              id="comment"
              onFocus={handleFocus}
              onBlur={handleBlur}
              className="p-2 px-3 block ring-0 w-full resize-none border-0 bg-transparent text-gray-900 placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
              placeholder="Add your comment..."
              value={comment}
              onChange={(e) => {
                setComment(e.currentTarget.value);
              }}
              defaultValue={""}
              autoFocus={false}
            />
          </div>

          <div
            className={`ring-0  absolute inset-x-0 bottom-0 flex justify-between items-center py-2 pl-3 pr-2 transition-all ease-in-out ${isFocused
              ? "opacity-100 visible duration-300 delay-100 "
              : " opacity-0 invisible duration-150 delay-0"
              }`}
          >
            <div className="flex-1">
              <Pills>
                {files.map((file) => {
                  return (
                    <FilePill
                      id={file.id}
                      name={file.name}
                      type={file.fileType}
                      // checkFileAccessAccountId={props.question.createdBy.id}
                      useSlideOver={true}
                      onClick={() => {
                        setFiles(files.filter((f) => f.id !== file.id));
                      }}
                    />
                  );
                })}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenModal("add-document");
                  }}
                  className="bg-gray-100 border hover:border-gray-400 hover:shadow-sm
                     flex items-center rounded-2xl px-2 py-1"
                >
                  <PlusIcon className="h-5 w-5 text-gray-600/80" />
                  <p className="ml-1 text-xs text-gray-600 truncate">
                    Add document
                  </p>
                </button>
              </Pills>
            </div>
            <div className="ml-2">
              <Button
                ref={createCommentRef} // Add ref
                variant="neutral"
                size="s"
                text="Comment"
                isLoading={createComment.isPending}
                loadingText="Commenting..."
                type="submit"
              />
            </div>
          </div>
        </form>
        <AddFileModal
          open={openModal === "add-document"}
          onClose={() => {
            setOpenModal("");
          }}
          onFilesSelected={(files) => {
            setFiles(files.map((f) => f.file as DataRoomFile));
          }}
        />
      </div>
    </>
  );
}

function UseAnswerButton(props: {
  onClick: () => void;
}) {
  const [state, setState] = useState<"idle" | "added">("idle");

  const handleClick = useCallback(() => {
    props.onClick();
    setState("added");
    setTimeout(() => setState("idle"), 1800);
  }, [props.onClick]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-x-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 ${state === "added"
        ? "bg-green-50 text-green-700 ring-1 ring-green-200"
        : "bg-persian-50 text-persian-700 ring-1 ring-persian-200 hover:bg-persian-100 hover:ring-persian-300"
        }`}
    >
      {state === "added" ? (
        <>
          <CheckIcon className="w-3.5 h-3.5" />
          Added
        </>
      ) : (
        <>
          <SparklesIcon className="w-3.5 h-3.5" />
          Use as answer
        </>
      )}
    </button>
  );
}

function RelevantFilePillWithAdd(props: {
  file: { id: string; name: string; fileType: FileType; folderId: string };
  isAdded: boolean;
  onFileClick: () => void;
  onAddFile: () => void;
}) {
  const [justAdded, setJustAdded] = useState(false);
  const showCheck = props.isAdded || justAdded;

  return (
    <div className="group/rfpill relative inline-flex items-center">
      <FilePill
        id={props.file.id}
        name={props.file.name}
        type={props.file.fileType}
        showDetailsCard={false}
        onClick={props.onFileClick}
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (props.isAdded) return;
          setJustAdded(true);
          props.onAddFile();
          setTimeout(() => setJustAdded(false), 1500);
        }}
        className={`ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 transition-all duration-150 ${showCheck
          ? "bg-green-100 text-green-600"
          : "bg-gray-100 text-gray-500 opacity-0 group-hover/rfpill:opacity-100 hover:bg-persian-100 hover:text-persian-600"
          }`}
        title={showCheck ? "Added to answer" : "Add to answer"}
      >
        {showCheck ? (
          <CheckIcon className="w-3 h-3" />
        ) : (
          <PlusIcon className="w-3 h-3" />
        )}
      </button>
    </div>
  );
}

function SuggestedAnswerAgent(props: {
  question: QuestionQuery["question"];
  onFileClick: (data: InlineFileClickData) => void;
  onAddFile: (data: InlineFileClickData) => void;
  onUseAnswer: (text: string, files: InlineFileClickData[]) => void;
  addedFileKeys: Set<string>;
}) {
  const suggestedAnswer = props.question.suggestedAnswer;

  if (!suggestedAnswer) {
    return null;
  }

  const status = suggestedAnswer.status;
  const blocks = suggestedAnswer.blocks ?? [];
  const textBlocks = blocks.filter((b) => b.type === SuggestedAnswerBlockType.Text);
  const toolAndReasoningBlocks = blocks.filter(
    (b) =>
      b.type === SuggestedAnswerBlockType.ToolCall ||
      b.type === SuggestedAnswerBlockType.Reasoning,
  );
  const relevantFiles = suggestedAnswer.relevantFiles ?? [];
  const isStreaming =
    status === SuggestedAnswerStatus.Initial ||
    status === SuggestedAnswerStatus.Searching;
  const isComplete = status === SuggestedAnswerStatus.Complete;

  if (status === SuggestedAnswerStatus.Failed) {
    return (
      <div className="mt-4 p-4 rounded-md border border-red-200 bg-red-50">
        <div className="flex items-center space-x-2">
          <BotIcon className="w-4 h-4 text-red-600" />
          <p className="text-sm text-red-700 font-semibold">
            Failed to generate suggested answer
          </p>
        </div>
      </div>
    );
  }

  if (
    isComplete &&
    textBlocks.length === 0 &&
    relevantFiles.length === 0
  ) {
    return (
      <div className="mt-4 p-4 rounded-md border border-concrete-100">
        <div className="flex items-center space-x-2">
          <BotIcon className="w-4 h-4 text-persian-900" />
          <p className="text-sm text-persian-900 font-semibold">
            No relevant references found
          </p>
        </div>
      </div>
    );
  }

  const hasContent = textBlocks.length > 0 || toolAndReasoningBlocks.length > 0 || isStreaming;

  const handleFileRefClick = useCallback(
    (data: FileRefClickData) => {
      props.onFileClick({
        fileId: data.fileId,
        fileName: data.fileName,
        fileType: data.fileType,
        folderId: data.folderId,
        pageIndex: data.pageIndex,
        rectsOnPage: data.rectsOnPage,
      });
    },
    [props.onFileClick],
  );

  const handleUseAnswer = () => {
    const rawText = textBlocks
      .map((b) => b.text ?? "")
      .filter(Boolean)
      .join("\n\n");

    const allFiles: InlineFileClickData[] = relevantFiles.map((f) => ({
      fileId: f.id,
      fileName: f.name,
      fileType: f.fileType,
      folderId: f.folderId,
      pageIndex: 0,
    }));

    props.onUseAnswer(rawText, allFiles);
  };

  return (
    <div className="mt-4 space-y-3">
      {hasContent && (
        <div className="p-4 rounded-md border border-concrete-100 bg-white space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BotIcon className="w-4 h-4 text-persian-900" />
              <p className="text-sm font-semibold text-persian-900">
                Suggestions
              </p>
              {isStreaming && textBlocks.length === 0 && toolAndReasoningBlocks.length === 0 && (
                <Spinner color="gray" size="s" />
              )}
            </div>
            {isComplete && textBlocks.length > 0 && (
              <UseAnswerButton onClick={handleUseAnswer} />
            )}
          </div>

          {toolAndReasoningBlocks.length > 0 && (
            <SuggestedAnswerAgentBox
              blocks={toolAndReasoningBlocks}
              isStreaming={isStreaming}
            />
          )}

          {textBlocks.length > 0 && (
            <FileRefClickProvider value={handleFileRefClick}>
              <div className="text-sm text-gray-700 break-words leading-relaxed space-y-2">
                {textBlocks.map((block, i) => (
                  <AnswerViewer key={block.toolCallId ?? `text-${i}`} content={block.text ?? ""} />
                ))}
              </div>
            </FileRefClickProvider>
          )}

          {relevantFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {relevantFiles.map((file) => (
                <RelevantFilePillWithAdd
                  key={file.id}
                  file={file}
                  isAdded={props.addedFileKeys.has(fileRefKey({ fileId: file.id, pageIndex: 0 }))}
                  onFileClick={() =>
                    props.onFileClick({
                      fileId: file.id,
                      fileName: file.name,
                      fileType: file.fileType,
                      folderId: file.folderId,
                      pageIndex: 0,
                    })
                  }
                  onAddFile={() =>
                    props.onAddFile({
                      fileId: file.id,
                      fileName: file.name,
                      fileType: file.fileType,
                      folderId: file.folderId,
                      pageIndex: 0,
                    })
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const HIDDEN_TOOL_CALLS = new Set(["add_files"]);

function SuggestedAnswerAgentBox(props: {
  blocks: NonNullable<QuestionQuery["question"]["suggestedAnswer"]>["blocks"];
  isStreaming: boolean;
}) {
  const { blocks, isStreaming } = props;
  const [expanded, setExpanded] = useState(false);

  const toolCallBlocks = blocks.filter(
    (b) => b.type === SuggestedAnswerBlockType.ToolCall,
  );
  const visibleToolCallBlocks = toolCallBlocks.filter(
    (b) => !HIDDEN_TOOL_CALLS.has(b.toolName ?? ""),
  );
  const reasoningBlocks = blocks.filter(
    (b) => b.type === SuggestedAnswerBlockType.Reasoning,
  );

  const hasPendingCalls = toolCallBlocks.some((b) => !b.toolResult);
  const isLive = isStreaming || hasPendingCalls;

  const latestVisibleToolCall = visibleToolCallBlocks.length > 0
    ? visibleToolCallBlocks[visibleToolCallBlocks.length - 1]
    : null;
  const activeReasoning = reasoningBlocks.find((b) => !b.durationMs);
  const completedReasoning = reasoningBlocks.filter((b) => !!b.durationMs);

  const totalReasoningMs = completedReasoning.reduce(
    (sum, b) => sum + (b.durationMs ?? 0),
    0,
  );

  if (isLive) {
    return (
      <div className="rounded-md border border-concrete-200 bg-concrete-50 p-3 space-y-2">
        {activeReasoning && (
          <AiMessageReasoningBlock reasoning={activeReasoning.reasoning} durationMs={activeReasoning.durationMs} />
        )}
        {latestVisibleToolCall && (
          <AiMessageToolCallBlock block={latestVisibleToolCall} />
        )}
        {!activeReasoning && !latestVisibleToolCall && (
          <div className="flex items-center space-x-2">
            <BotIcon className="w-4 h-4 text-persian-500" />
            <p className="text-sm text-persian-500 font-semibold">
              Working...
            </p>
            <Spinner color="gray" size="s" />
          </div>
        )}
      </div>
    );
  }

  const summaryTokens = buildToolSummaryTokens(toolCallBlocks);
  if (totalReasoningMs > 0) {
    const formatted = formatReasoningDurationMs(totalReasoningMs);
    if (formatted) {
      summaryTokens.push({
        icon: BrainIcon,
        label: `Thought for ${formatted}`,
      });
    }
  }

  if (summaryTokens.length === 0 && completedReasoning.length === 0) {
    return null;
  }

  return (
    <div className="rounded-md border border-concrete-200 bg-concrete-50 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between gap-x-2 px-2.5 py-1.5 hover:bg-concrete-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-x-1 min-w-0 flex-wrap text-sm">
          {summaryTokens.map((token, i) => {
            const IconComp = token.icon;
            return (
              <span key={`${token.label}-${i}`} className="inline-flex items-center gap-x-1 text-concrete-600">
                {i > 0 && <span className="text-concrete-300 mx-0.5">&middot;</span>}
                <IconComp className="w-3.5 h-3.5 text-persian-900 flex-shrink-0" />
                <span className="font-medium text-concrete-700">{token.label}</span>
              </span>
            );
          })}
        </div>
        {expanded ? (
          <ChevronUpIcon className="h-4 w-4 text-concrete-500 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 text-concrete-500 flex-shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="border-t border-concrete-200 p-3 space-y-2">
          {completedReasoning.map((block, i) => (
            <AiMessageReasoningBlock
              key={`reasoning-${i}`}
              reasoning={block.reasoning}
              durationMs={block.durationMs}
            />
          ))}
          {visibleToolCallBlocks.map((block, i) => (
            <div key={block.toolCallId ?? `tool-${i}`} className="w-full min-w-0">
              <AiMessageToolCallBlock block={block} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferencePill(props: {
  reference: AnswerReference;
  onReferenceSelected: (reference: AnswerReference) => void;
}) {
  return (
    <button
      onClick={() => {
        props.onReferenceSelected(props.reference);
      }}
      className="flex flex-wrap group justify-center text-left relative"
    >
      <div className="cursor-pointer hover:border-gray-400 hover:shadow-sm rounded-2xl px-2 py-1 bg-white border border-gray-200 flex items-center space-x-2">
        <FileIcon size="s" fileType={props.reference.file.fileType} />
        <p className="text-xs ml-1 text-gray-800 truncate">
          {props.reference.file.name}
        </p>
        {props.reference.pageIndex !== undefined ? (
          <p className="text-xs truncate text-gray-500/80 ml-1 inline">
            Page {props.reference.pageIndex + 1}
          </p>
        ) : null}
      </div>
      {props.reference.quote ? (
        <span
          className={`transition-all p-3 z-20 delay-300 absolute bottom-8 rounded-md shadow scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100 border border-gray-200 bg-white w-64`}
        >
          <div className="p-3 rounded-md border border-gray-200">
            <p className="text-xs font-semibold text-gray-700">
              {props.reference.quote}
            </p>
          </div>
        </span>
      ) : null}
    </button>
  );
}

function AnswerThreadCommentForm(props: {
  questionAnswerId: string;
  questionId: string;
  createdBy: { id: string; name: string }; // For file access checks potentially
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const account = useSelector(authSelectors.account);
  const createThreadMessage =
    useCreateQuestionAnswerThreadMessageMutation(client);

  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<AnswerReference[]>([]); // Use AnswerReference for consistency
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<
    "add-document" | "upload-document" | ""
  >("");
  const [isFocused, setIsFocused] = useState(false); // State for focus

  const textAreaRef = useRef<HTMLTextAreaElement>(null); // Ref for textarea
  const createCommentRef = useRef<HTMLButtonElement>(null); // Ref for submit button

  useEffect(() => {
    // Focus textarea after modal closes
    if (!openModal && textAreaRef.current && isFocused) {
      const timer = setTimeout(() => {
        textAreaRef.current?.focus();
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [openModal, isFocused]);

  const handleKeyDown = (event: KeyboardEvent) => {
    // Submit on Enter (not Shift+Enter)
    if (event.key === "Enter" && !event.shiftKey && isFocused) {
      event.preventDefault();
      if (createCommentRef.current) {
        createCommentRef.current.click();
      }
    }
  };

  useEffect(() => {
    // Add/remove keyboard listener
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFocused, comment, files]); // Re-add listener if dependencies change

  const clearForm = () => {
    setComment("");
    setFiles([]);
    setError(null);
    setIsFocused(false); // Unfocus after submit
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() && files.length === 0) {
      setError("Comment cannot be empty unless attaching a file.");
      return;
    }
    setError(null);

    createThreadMessage.mutate(
      {
        input: {
          questionAnswerID: props.questionAnswerId,
          answer: comment,
          dataRoomFileIDs: files.map((f) => f.file.id),
        },
      },
      {
        onSuccess: () => {
          clearForm();
          queryClient.invalidateQueries({
            queryKey: ["Question", { id: props.questionId }],
          });
          toasts.success("Comment added");
        },
        onError: (err) => {
          setError("Failed to add comment. Please try again.");
          console.error("Error adding comment:", err);
          toasts.error("Failed to add comment");
        },
      },
    );
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    // Use setTimeout to allow clicking buttons within the form before blur hides them
    setTimeout(() => {
      if (
        !document.activeElement ||
        !document.activeElement.closest(
          `#comment-form-${props.questionAnswerId}`,
        )
      ) {
        setIsFocused(false);
        if (error) setError(null); // Clear error on blur if input is empty/valid
      }
    }, 0);
  };

  if (!account) {
    return null;
  }

  // Use same AddFileModal as Activity component for consistency if needed
  // (Currently AddFileModal doesn't exist in scope here, needs import or prop drilling)

  return (
    <div className="flex gap-x-3" onBlur={handleBlur}>
      {" "}
      {/* Add onBlur here */}
      <form
        id={`comment-form-${props.questionAnswerId}`} // Unique ID for blur check
        onSubmit={handleSubmit}
        className="relative flex-auto"
      >
        <div
          className={`overflow-hidden rounded-lg ${isFocused ? "pb-12" : "pb-1" // Dynamic padding bottom like Activity
            } shadow-sm ring-1 ring-inset transition-all ease-in-out duration-300 ring-concrete-100 focus-within:bg-white focus-within:ring-persian-600 focus-within:ring-1`} // Adjusted focus ring
        >
          <label
            htmlFor={`comment-${props.questionAnswerId}`}
            className="sr-only"
          >
            Add comment
          </label>
          {/* Use textarea like Activity, or keep TextareaAutosize */}
          {/* Using textarea for closer match to Activity */}
          <textarea
            ref={textAreaRef}
            rows={isFocused ? 2 : 1} // Dynamic rows like Activity
            name={`comment-${props.questionAnswerId}`}
            id={`comment-${props.questionAnswerId}`}
            onFocus={handleFocus}
            // onBlur={handleBlur} // Moved onBlur to parent div
            className={`p-2 px-3 shadow-sm block w-full no-scrollbar resize-none border-0 bg-transparent text-gray-900 placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6 ${error ? "ring-1 ring-red-500" : "" // Simplified error indication
              }`}
            placeholder="Add to answer..."
            value={comment}
            onChange={(e) => {
              setComment(e.currentTarget.value);
              if (error) setError(null);
            }}
            defaultValue={""}
          />
          {error &&
            isFocused && ( // Show error only when focused
              <div className="px-3 pb-1 text-red-500 text-xs">{error}</div>
            )}
        </div>

        {/* Absolutely positioned controls like Activity */}
        <div
          className={`absolute inset-x-0 bottom-0 flex justify-end py-2 pl-3 pr-2 transition-all ease-in-out ${isFocused
            ? "opacity-100 visible duration-300 delay-100"
            : "opacity-0 invisible duration-150 delay-0"
            }`}
        >
          <div className="flex items-center">
            <Button
              ref={createCommentRef} // Add ref
              isLoading={createThreadMessage.isPending}
              loadingText="Sending..."
              type="submit"
              variant="neutral"
              text="Comment"
              size="s"
            />
          </div>
        </div>
      </form>
      {/* Needs AddFileModal instance here or passed as prop */}
      {/* <AddFileModal
          open={openModal === "add-document"}
          onClose={() => {
            setOpenModal("");
          }}
          onFilesSelected={(selectedFiles) => {
             // Logic to convert selectedFiles (likely {file: DataRoomFile}[])
             // to AnswerReference[] and update state
             const newAnswerRefs = selectedFiles.map(f => ({
               file: f.file as DataRoomFile,
               pageIndex: 0,
               rectsOnPage: [],
               quote: "",
             }));
            // Avoid duplicates if necessary
            const uniqueNewRefs = newAnswerRefs.filter(newRef => !files.some(existingRef => existingRef.file.id === newRef.file.id));
            setFiles([...files, ...uniqueNewRefs]);
          }}
        /> */}
    </div>
  );
}

function CopyAnswerButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(stripInlineRefs(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-x-1 text-xs text-concrete-800 hover:text-concrete-700 transition-colors"
    >
      {copied ? (
        <>
          <CheckIcon className="w-3.5 h-3.5" />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <CopyIcon className="w-3.5 h-3.5" />
          <span>Copy answer</span>
        </>
      )}
    </button>
  );
}

function CopyFilesButton({
  files,
  dealId,
}: {
  files: NonNullable<
    NonNullable<QuestionQuery["question"]["answers"]>[0]
  >["answerFiles"];
  dealId: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!files) return;

    const baseUrl = window.location.origin;

    const fileData = files.map((fileRef) => {
      const index = getDataRoomIndex(
        fileRef.file.index?.sortIndex,
        fileRef.file.parentFolders,
        "file",
      );
      const displayText = index
        ? `${index} ${fileRef.file.name}`
        : fileRef.file.name;
      const fileUrl =
        baseUrl +
        links.fileLink(
          fileRef.file.id,
          fileRef.file.folderId,
          dealId,
          {
            pageIndex: fileRef.pageIndex ?? undefined,
            rectsOnPage: fileRef.rectsOnPage ?? undefined,
          },
        );
      return { displayText, fileUrl };
    });

    // Plain text fallback
    const plainText = fileData.map((f) => f.displayText).join("\n");

    // HTML with hyperlinks for Excel - wrap in table cell to keep in single cell
    const htmlLinks = fileData
      .map((f) => `<a href="${f.fileUrl}">${f.displayText}</a>`)
      .join("<br>");
    const html = `<table><tr><td>${htmlLinks}</td></tr></table>`;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([plainText], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback to plain text if ClipboardItem not supported
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-x-1 text-xs text-concrete-800 hover:text-concrete-700 transition-colors"
    >
      {copied ? (
        <>
          <CheckIcon className="w-3.5 h-3.5" />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <CopyIcon className="w-3.5 h-3.5" />
          <span>Copy references</span>
        </>
      )}
    </button>
  );
}
